import { IconButton, Tooltip } from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import classNames from 'classnames';
import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import * as MapGL from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { defaultBasemapStyle, MapStyle } from '~/common/types';
import Popover from '~/components2/Popover/Popover';
import rootStore from '~/store';
import historicalImageryStore from '../HistoricalImagery/store';
import images from './assets';
import { basemapStyles } from './common';
import { customBasemaps } from './custom';

interface TParams {
  projectId?: string;
  mapId?: string;
}

interface BasemapPickerProps {
  visible?: boolean;
  style?: MapStyle;
  onChange?: (style: MapStyle) => void;
  onStyleChangeStart?: () => void;
  allowHistoricalImagery?: boolean;
  mapIndex?: number;
  disabled?: boolean;
  /**
   * When set, only these options are shown (from `basemapStyles`); ESRI/Google satellites and
   * historical imagery rows are omitted. Use for constrained embeds (e.g. permit PDF map).
   */
  allowedBasemapStyles?: MapStyle[];
}

export const BasemapPicker = ({
  visible,
  style,
  onChange,
  onStyleChangeStart,
  allowHistoricalImagery = false,
  mapIndex = 0,
  disabled = false,
  allowedBasemapStyles,
}: BasemapPickerProps) => {
  const [mapStyle, setMapStyle] = useState<string>(style || defaultBasemapStyle);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { current: map } = MapGL.useMap();
  const params = useParams({ strict: false }) as TParams;
  const { googleSatellite, esriSatellite } = useSnapshot(customBasemaps) as typeof customBasemaps;
  const { historicalImageryMapIndices } = useSnapshot(historicalImageryStore);

  useEffect(() => {
    // Only sync the local state with the prop, don't trigger setStyle on mount
    // The Map component already handles the initial style
    if (style) {
      setMapStyle(style);
    }
  }, [style]);

  function updateBasemap(_style: string, _styleName?: string) {
    if (!_style || isEqual(mapStyle, _style)) return;
    if (!map) return;

    historicalImageryStore.reset(mapIndex);

    const mapInstance = map.getMap();

    const styleValues = Object.values(MapStyle);
    const styleIndex: number | undefined = styleValues.indexOf(_style as MapStyle);
    const styleName = _styleName || Object.keys(MapStyle)[styleIndex];
    const previousStyleIndex = styleValues.indexOf(mapStyle as MapStyle);
    const previousStyleName = previousStyleIndex !== -1 ? Object.keys(MapStyle)[previousStyleIndex] : mapStyle;

    const onStyleLoad = () => {
      // if passed in style is included in MapStyles enum, then save it locally.
      // We don't want to persist esri imagery locally.
      if (styleIndex !== -1) {
        onChange?.(_style as MapStyle);
      }

      captureTypedPosthogEvent(POSTHOG_EVENTS.projectBasemapChanged, {
        ...(params.projectId && { project_id: params.projectId }),
        organization_id: rootStore.currentOrganizationId || undefined,
        source: params.projectId ? 'project_details' : 'home',
        from: previousStyleName,
        to: styleName,
      });

      setMapStyle(_style);
    };

    const changeStyle = () => {
      onStyleChangeStart?.();
      mapInstance.once('style.load', onStyleLoad);
      mapInstance.setStyle(_style);
    };

    if (!mapInstance.isStyleLoaded()) {
      const onCurrentStyleLoad = () => {
        changeStyle();
      };
      mapInstance.once('style.load', onCurrentStyleLoad);

      if (mapInstance.isStyleLoaded()) {
        mapInstance.off('style.load', onCurrentStyleLoad);
        changeStyle();
      }
    } else {
      changeStyle();
    }
  }

  if (!visible) {
    return null;
  }

  const restrictedList =
    allowedBasemapStyles !== undefined && allowedBasemapStyles.length > 0
      ? basemapStyles.filter((s) => allowedBasemapStyles.includes(s.style as MapStyle))
      : null;
  const showEsriGoogleAndHistorical = restrictedList === null;

  return (
    <div className="z-10">
      <Popover
        side="right"
        sideOffset={10}
        align="end"
        trigger={(isOpen) => (
          // asChild so the popover's aria-expanded/controls and its focus restore land on the button.
          <Tooltip asChild side="right" sideOffset={10} text="Switch Basemap">
            <IconButton
              ref={triggerRef}
              variant="secondary"
              icon={isOpen ? 'chevronLeft' : 'dashboard'}
              size="icon"
              iconSize={14}
              disabled={disabled}
              loading={disabled}
              className="w-7 h-7"
              aria-label="Switch Basemap"
              data-testid="basemap-picker-trigger"
            />
          </Tooltip>
        )}
      >
        {(closePopover) => (
          <div className="shadow-bg-grey-70 z-10 flex max-h-[50vh] flex-col overflow-y-auto rounded bg-white p-2 shadow-lg">
            {(restrictedList ?? basemapStyles).map((s) => {
              return (
                <BasemapOption
                  key={s.style}
                  label={s.label}
                  img={s.img}
                  isCurrent={mapStyle === s.style}
                  onClick={() => {
                    updateBasemap(s.style);
                    // Popover's close is deferred, so it misses Radix's focus restore; do it here.
                    triggerRef.current?.focus();
                    closePopover();
                  }}
                />
              );
            })}

            {showEsriGoogleAndHistorical && (
              <>
                <BasemapOption
                  label="Satellite (ESRI)"
                  img={images.satelliteEsri}
                  isCurrent={isEqual(mapStyle, esriSatellite)}
                  isAvailable={esriSatellite !== undefined ? true : false}
                  onClick={() => {
                    if (esriSatellite) {
                      updateBasemap(esriSatellite as unknown as string, 'esriSatellite');
                      triggerRef.current?.focus();
                      closePopover();
                    }
                  }}
                />

                <BasemapOption
                  label="Satellite (Google)"
                  img={images.satelliteGoogle}
                  isCurrent={isEqual(mapStyle, googleSatellite)}
                  isAvailable={googleSatellite !== undefined ? true : false}
                  onClick={() => {
                    if (googleSatellite) {
                      updateBasemap(googleSatellite as unknown as string, 'googleSatellite');
                      triggerRef.current?.focus();
                      closePopover();
                    }
                  }}
                />
              </>
            )}

            {showEsriGoogleAndHistorical && allowHistoricalImagery && (
              <BasemapOption
                label={
                  historicalImageryMapIndices[mapIndex] === true ? 'Hide historical imagery' : 'View historical imagery'
                }
                img={images.satelliteEsri}
                isCurrent={false}
                isAvailable={allowHistoricalImagery === true}
                onClick={() => {
                  if (!historicalImageryMapIndices[mapIndex]) {
                    historicalImageryStore.setIsEnabled(mapIndex, true);
                  } else {
                    const opp: boolean = !historicalImageryMapIndices[mapIndex];
                    historicalImageryStore.setIsEnabled(mapIndex, opp);
                  }

                  triggerRef.current?.focus();
                  closePopover();
                }}
              />
            )}
          </div>
        )}
      </Popover>
    </div>
  );
};

interface BasemapOptionProps {
  label: string;
  img: string;
  isCurrent?: boolean;
  onClick?: () => void;
  isAvailable?: boolean;
}

const BasemapOption = ({ label, img, isCurrent, onClick, isAvailable = true }: BasemapOptionProps) => {
  if (!isAvailable) {
    return null;
  }

  const testId = `basemap-option-${label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}`;

  return (
    <button
      type="button"
      data-testid={testId}
      aria-current={isCurrent ? 'true' : undefined}
      onClick={() => {
        onClick?.();
      }}
      className={classNames(
        'w-full pointer-events-auto z-10 flex min-w-[250px] cursor-pointer items-center gap-4 rounded border-0 bg-transparent p-2 text-left text-2xs appearance-none hover:bg-secondary/10 hover:shadow-sm focus-ring',
        { 'bg-secondary/10 font-bold': isCurrent },
      )}
    >
      <img height={64} width={64} src={img} alt="" className="rounded-sm" />

      <span className="flex flex-col text-xs">{label}</span>
    </button>
  );
};
